import { useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

import type { Tag, TagCategoria, TagEquipe } from '@/api/tags';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TagChip } from '@/components/ui/tag-chip';
import { useTituloTagsPessoais } from '@/hooks/useTags';
import { normalize } from '@/lib/search/smartSearch';
import { agruparPorEscopo } from '@/lib/tags/agrupar';
import { cn } from '@/lib/utils';

interface TagFilterProps {
  tags: Tag[];
  categorias: TagCategoria[];
  /** Escritórios da pessoa — viram seções "Do escritório" quando há tag neles. */
  escritorios?: { id: string; nome: string }[];
  /** Equipes da pessoa — viram seções "Da equipe" quando há tag nelas. */
  equipes?: TagEquipe[];
  /** Ids das tags do filtro. */
  value: string[];
  onChange: (ids: string[]) => void;
  className?: string;
}

/** A partir de quantas tags a lista ganha o campo de busca. */
const TAGS_PARA_BUSCAR = 8;

/**
 * Filtro por tags, agrupado por categoria (PLANO_TAGS D7): OU dentro da categoria,
 * E entre categorias — a regra vem de quem aplica o filtro (`facetasDoFiltro` no
 * cliente, `tag_ids` no servidor). Some quando não há tag nenhuma.
 *
 * O gatilho é o mesmo do `FiltroDaLista` (docs/PLANO_BARRA_DE_LISTA.md): parado diz
 * "Tags"; com tags marcadas mostra quais ("contrato +2") e fica destacado. A lista
 * aceita várias — cada tag é uma caixa de marcar, e a lista fica aberta entre um
 * toque e outro. "Todas as tags", no topo, tira o filtro.
 */
export function TagFilter({ tags, categorias, escritorios = [], equipes, value, onChange, className }: TagFilterProps) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const tituloPessoal = useTituloTagsPessoais();
  const termo = normalize(busca.trim());
  const visiveis = useMemo(
    () => (termo ? tags.filter((t) => normalize(t.nome).includes(termo)) : tags),
    [tags, termo],
  );
  const secoes = useMemo(
    () =>
      agruparPorEscopo(visiveis, categorias, escritorios, { tituloPessoal, equipes }).filter(
        (s) => s.grupos.length > 0,
      ),
    [visiveis, categorias, escritorios, tituloPessoal, equipes],
  );
  const selecionadas = useMemo(() => new Set(value), [value]);
  const nomes = useMemo(() => tags.filter((t) => selecionadas.has(t.id)).map((t) => t.nome), [tags, selecionadas]);

  if (tags.length === 0 && value.length === 0) return null;

  const ativo = value.length > 0;
  const rotulo = !ativo
    ? 'Tags'
    : nomes.length === 0
      ? `${value.length} ${value.length === 1 ? 'tag' : 'tags'}`
      : nomes.length === 1
        ? nomes[0]
        : `${nomes[0]} +${nomes.length - 1}`;

  const alternar = (id: string) =>
    onChange(selecionadas.has(id) ? value.filter((v) => v !== id) : [...value, id]);

  return (
    <Popover
      open={aberto}
      onOpenChange={(abrir) => {
        setAberto(abrir);
        if (!abrir) setBusca('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={aberto}
          aria-label={ativo ? `Tags: ${nomes.join(', ') || rotulo}` : 'Tags'}
          className={cn(
            'h-11 max-w-[16rem] shrink-0 justify-between gap-2 px-3 font-normal sm:h-9',
            ativo && 'border-primary/50 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary',
            className,
          )}
        >
          <span className="truncate">{rotulo}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(18rem,calc(100vw-2rem))] p-0">
        {tags.length > TAGS_PARA_BUSCAR && (
          <div className="border-b p-2">
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar tags…"
              aria-label="Buscar tags"
              className="h-9"
            />
          </div>
        )}
        <div className="max-h-[min(60vh,22rem)] overflow-y-auto p-1">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              onChange([]);
              setAberto(false);
            }}
            className="min-h-10 w-full justify-start gap-2 px-2 font-normal"
          >
            <Check className={cn('h-4 w-4', ativo ? 'opacity-0' : 'opacity-100')} aria-hidden />
            Todas as tags
          </Button>
          <p className="px-2 pb-1 pt-2 text-xs text-muted-foreground">
            Na mesma categoria, mostra qualquer uma; entre categorias, todas.
          </p>
          {secoes.length === 0 && <p className="px-2 py-3 text-sm text-muted-foreground">Nada encontrado.</p>}
          {secoes.map((secao) => (
            <div key={secao.chave} className="pb-1">
              {secoes.length > 1 && (
                <p className="px-2 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {secao.titulo}
                </p>
              )}
              {secao.grupos.map((grupo) => (
                <div key={grupo.categoria?.id ?? 'sem'} className="pt-1">
                  <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
                    {grupo.categoria?.nome ?? 'Sem categoria'}
                  </p>
                  {grupo.tags.map((tag) => {
                    const marcada = selecionadas.has(tag.id);
                    return (
                      <Button
                        key={tag.id}
                        type="button"
                        variant="ghost"
                        role="checkbox"
                        aria-checked={marcada}
                        onClick={() => alternar(tag.id)}
                        className="min-h-10 w-full justify-start gap-2 px-2 font-normal"
                      >
                        <span
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                            marcada ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/50',
                          )}
                          aria-hidden
                        >
                          {marcada && <Check className="h-3 w-3" />}
                        </span>
                        <TagChip tag={tag} />
                      </Button>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
